import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  api,
  type AdminUser,
  type SalesOrg,
  type UserAccessProfile,
  type UserRole,
} from "../lib/api";
import { isAdmin, isSuperAdmin, useAuth } from "../lib/auth";
import { TOOLS } from "../lib/atomika-tools";
import {
  PASSWORD_MISMATCH,
  PASSWORD_REQUIREMENT,
  validatePassword,
} from "../lib/password";
import { PasswordChecklist } from "../components/PasswordChecklist";
import { PasswordInput } from "../components/PasswordInput";

export const Route = createFileRoute("/admin/notendur")({
  component: AdminUsersPage,
});

const AREAS = TOOLS.map((t) => ({ id: t.id, name: t.name }));

const ROLE_LABEL: Record<UserRole, string> = {
  superadmin: "Yfirstjórnandi",
  admin: "Stjórnandi",
  user: "Notandi",
};

const SALES_ORG_LABEL: Record<SalesOrg, string> = {
  laufid: "Laufi\u00f0",
  atomika: "Atomika",
};

const card = "rounded-2xl border border-line bg-white p-5 shadow-sm";
const input =
  "mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
const label = "block text-[12.5px] font-semibold text-ink";

function AdminUsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminUser | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.admin.listUsers();
      setUsers(res.users);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!isAdmin(me)) {
    return (
      <div className="mx-auto max-w-lg px-6 py-24 text-center">
        <h1 className="text-xl font-bold text-ink">Enginn aðgangur</h1>
        <p className="mt-2 text-sm text-muted-2">
          Aðeins stjórnendur hafa aðgang að notendaumsjón.
        </p>
      </div>
    );
  }

  const roleOptions: UserRole[] = isSuperAdmin(me)
    ? ["user", "admin", "superadmin"]
    : ["user"];
  const canManage = (u: AdminUser) => isSuperAdmin(me) || u.role === "user";

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold text-ink">Notendaumsjón</h1>
      <p className="mt-1 text-sm text-muted-2">
        Stofnaðu notendur og stýrðu aðgangi þeirra að herbergjunum.
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[360px_1fr]">
        <CreateUserPanel roleOptions={roleOptions} onCreated={load} />

        <div className={card}>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-bold text-ink">Notendur</h2>
            <span className="text-[12.5px] text-muted-3">{users.length}</span>
          </div>

          {loading ? (
            <p className="text-sm text-muted-3">Hleð…</p>
          ) : error ? (
            <p className="text-sm text-danger">{error}</p>
          ) : (
            <div className="divide-y divide-line">
              {users.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center gap-3 py-3 first:pt-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-ink">
                        {u.displayName || u.email}
                      </span>
                      {!u.isActive && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-semibold text-muted-2">
                          óvirkur
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-[12px] text-muted-3">
                      {u.email} ·{" "}
                      {u.accessProfile === "salesperson"
                        ? `Sölumaður – ${SALES_ORG_LABEL[u.salesOrg]}`
                        : ROLE_LABEL[u.role]}{" "}
                      ·{" "}
                      {u.areaIds === "*"
                        ? "öll svæði"
                        : `${u.areaIds.length} svæði`}
                    </div>
                  </div>
                  {canManage(u) && u.id !== me?.id && (
                    <button
                      onClick={() => setEditing(u)}
                      className="rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold text-ink hover:bg-canvas"
                    >
                      Breyta
                    </button>
                  )}
                  {u.id === me?.id && (
                    <span className="text-[11.5px] text-muted-3">þú</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <EditUserModal
          user={editing}
          roleOptions={roleOptions}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function CreateUserPanel({
  roleOptions,
  onCreated,
}: {
  roleOptions: UserRole[];
  onCreated: () => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState<UserRole>("user");
  const [salesOrg, setSalesOrg] = useState<SalesOrg>("laufid");
  const [accessProfile, setAccessProfile] =
    useState<UserAccessProfile>("salesperson");
  const [areaIds, setAreaIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function toggleArea(id: string) {
    setAreaIds((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setOk(null);
    if (validatePassword(password)) {
      setError(PASSWORD_REQUIREMENT);
      return;
    }
    if (password !== confirmPassword) {
      setError(PASSWORD_MISMATCH);
      return;
    }
    setSaving(true);
    try {
      await api.admin.createUser({
        email,
        displayName: displayName || undefined,
        password,
        role,
        salesOrg,
        accessProfile,
        areaIds:
          role === "user" && accessProfile === "custom" ? areaIds : undefined,
      });
      setOk(
        `Notandi ${email} stofnaður. Hann þarf að velja nýtt lykilorð við fyrstu innskráningu.`,
      );
      setEmail("");
      setDisplayName("");
      setPassword("");
      setConfirmPassword("");
      setRole("user");
      setSalesOrg("laufid");
      setAccessProfile("salesperson");
      setAreaIds([]);
      await onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className={`${card} h-fit`}>
      <h2 className="mb-4 text-base font-bold text-ink">Nýr notandi</h2>
      <div className="space-y-3.5">
        <div>
          <label className={label}>Nafn</label>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            className={input}
          />
        </div>
        <div>
          <label className={label}>Netfang</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={input}
          />
        </div>
        <div>
          <label className={label}>Tímabundið lykilorð</label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className={input}
          />
          <PasswordChecklist password={password} confirm={confirmPassword} />
        </div>
        <div>
          <label className={label}>Staðfestu lykilorð</label>
          <PasswordInput
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            placeholder="Sláðu inn sama lykilorð aftur"
            className={input}
          />
        </div>
        <div>
          <label className={label}>Hlutverk</label>
          <select
            value={accessProfile}
            onChange={(e) => {
              const profile = e.target.value as UserAccessProfile;
              setAccessProfile(profile);
              if (profile === "salesperson") setRole("user");
            }}
            className={input}
          >
            <option value="salesperson">{"S\u00f6luma\u00f0ur"}</option>
            <option value="custom">{"S\u00e9rsni\u00f0inn notandi"}</option>
          </select>
        </div>
        {accessProfile === "custom" && (
          <div>
            <label className={label}>Kerfishlutverk</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className={input}
            >
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </div>
        )}
        {accessProfile === "salesperson" && (
          <div>
            <label className={label}>{"S\u00f6luteymi"}</label>
            <select
              value={salesOrg}
              onChange={(e) => setSalesOrg(e.target.value as SalesOrg)}
              className={input}
            >
              {(Object.keys(SALES_ORG_LABEL) as SalesOrg[]).map((org) => (
                <option key={org} value={org}>
                  {SALES_ORG_LABEL[org]}
                </option>
              ))}
            </select>
            <p className="mt-2 text-[11.5px] text-muted-2">
              {"Fastur a\u00f0gangur: Reikniv\u00e9l og Atomika CRM."}
            </p>
          </div>
        )}

        {role === "user" && accessProfile === "custom" && (
          <div>
            <label className={label}>Aðgangur að Herbergjum</label>
            <div className="mt-1.5 space-y-1.5">
              {AREAS.map((a) => (
                <label
                  key={a.id}
                  className="flex cursor-pointer items-center gap-2 text-[13px] text-ink"
                >
                  <input
                    type="checkbox"
                    checked={areaIds.includes(a.id)}
                    onChange={() => toggleArea(a.id)}
                    className="h-4 w-4 rounded border-line accent-brand"
                  />
                  {a.name}
                </label>
              ))}
            </div>
          </div>
        )}

        {error && <p className="text-[12.5px] text-danger">{error}</p>}
        {ok && <p className="text-[12.5px] text-[#16A75C]">{ok}</p>}

        <button
          type="submit"
          disabled={saving}
          className="w-full rounded-lg bg-brand px-4 py-2 font-semibold text-white transition hover:bg-brand-dark disabled:opacity-50"
        >
          {saving ? "Stofna…" : "Stofna notanda"}
        </button>
      </div>
    </form>
  );
}

function EditUserModal({
  user,
  roleOptions,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  roleOptions: UserRole[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState(user.displayName ?? "");
  const [role, setRole] = useState<UserRole>(user.role);
  const [salesOrg, setSalesOrg] = useState<SalesOrg>(user.salesOrg);
  const [accessProfile, setAccessProfile] = useState<UserAccessProfile>(
    user.accessProfile,
  );
  const [isActive, setIsActive] = useState(user.isActive);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [areaIds, setAreaIds] = useState<string[]>(
    user.areaIds === "*" ? [] : user.areaIds,
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cred, setCred] = useState<{
    email: string;
    tempPassword: string;
    loginUrl: string;
    emailSent: boolean;
  } | null>(null);

  function toggleArea(id: string) {
    setAreaIds((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  }

  async function sendAccess() {
    setError(null);
    setSending(true);
    try {
      const res = await api.admin.sendCredentials(user.id);
      setCred(res);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  async function copyCredentials() {
    if (!cred) return;
    const text = `Slóð: ${cred.loginUrl}\nNotandanafn: ${cred.email}\nLykilorð: ${cred.tempPassword}`;
    const ok = await navigator.clipboard.writeText(text).then(
      () => true,
      () => false,
    );
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  async function save() {
    setError(null);
    if (newPassword && validatePassword(newPassword)) {
      setError(PASSWORD_REQUIREMENT);
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      setError(PASSWORD_MISMATCH);
      return;
    }
    setSaving(true);
    try {
      await api.admin.updateUser(user.id, {
        displayName,
        role,
        salesOrg,
        accessProfile,
        isActive,
        password: newPassword || undefined,
      });
      if (role === "user" && accessProfile === "custom") {
        await api.admin.setAccess(user.id, areaIds);
      }
      await onSaved();
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Eyða notanda ${user.email}?`)) return;
    setSaving(true);
    try {
      await api.admin.deleteUser(user.id);
      await onSaved();
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/40 px-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-line bg-white p-6 shadow-[0_24px_60px_rgba(11,36,71,.2)]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-ink">Breyta notanda</h2>
        <p className="mt-1 text-[12.5px] text-muted-3">{user.email}</p>

        <div className="mt-5 space-y-3.5">
          <div>
            <label className={label}>Nafn</label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className={input}
            />
          </div>
          <div>
            <label className={label}>Hlutverk</label>
            <select
              value={accessProfile}
              onChange={(e) => {
                const profile = e.target.value as UserAccessProfile;
                setAccessProfile(profile);
                if (profile === "salesperson") setRole("user");
              }}
              className={input}
            >
              <option value="salesperson">{"S\u00f6luma\u00f0ur"}</option>
              <option value="custom">{"S\u00e9rsni\u00f0inn notandi"}</option>
            </select>
          </div>
          {accessProfile === "custom" && (
            <div>
              <label className={label}>Kerfishlutverk</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
                className={input}
              >
                {roleOptions.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className={label}>{"S\u00f6luteymi"}</label>
            <select
              value={salesOrg}
              onChange={(e) => setSalesOrg(e.target.value as SalesOrg)}
              className={input}
            >
              {(Object.keys(SALES_ORG_LABEL) as SalesOrg[]).map((org) => (
                <option key={org} value={org}>
                  {SALES_ORG_LABEL[org]}
                </option>
              ))}
            </select>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-[13px] font-semibold text-ink">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-line accent-brand"
            />
            Virkur aðgangur
          </label>
          <div>
            <label className={label}>Nýtt lykilorð (valfrjálst)</label>
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Skildu eftir autt til að halda óbreyttu"
              className={input}
            />
            {newPassword ? (
              <PasswordChecklist
                password={newPassword}
                confirm={confirmPassword}
              />
            ) : (
              <p className="mt-1 text-[11px] text-muted-3">
                Ef sett: notandinn er þvingaður til að velja nýtt við næstu
                innskráningu.
              </p>
            )}
          </div>
          {newPassword && (
            <div>
              <label className={label}>Staðfestu nýtt lykilorð</label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Sláðu inn sama lykilorð aftur"
                className={input}
              />
            </div>
          )}

          <div className="rounded-xl border border-line bg-canvas p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[12.5px] font-semibold text-ink">
                  Senda aðgangsupplýsingar
                </p>
                <p className="mt-0.5 text-[11.5px] text-muted-3">
                  Býr til nýtt bráðabirgðalykilorð, sendir notandanum slóð +
                  notandanafn + lykilorð í tölvupósti og þvingar breytingu við
                  fyrstu innskráningu.
                </p>
              </div>
              <button
                type="button"
                onClick={sendAccess}
                disabled={sending}
                className="shrink-0 rounded-lg border border-line bg-white px-3 py-2 text-[12.5px] font-semibold text-brand hover:bg-canvas disabled:opacity-50"
              >
                {sending ? "Sendi…" : "Senda aðgang"}
              </button>
            </div>

            {cred && (
              <div className="mt-3 rounded-lg border border-line bg-white p-3 text-[12.5px]">
                <p
                  className={
                    cred.emailSent ? "text-[#16A75C]" : "text-[#B45309]"
                  }
                >
                  {cred.emailSent
                    ? `Tölvupóstur sendur á ${cred.email}.`
                    : "Tölvupóstur er ekki stilltur — afritaðu upplýsingarnar og sendu handvirkt."}
                </p>
                <dl className="mt-2 space-y-1 text-ink">
                  <div className="flex gap-2">
                    <dt className="w-24 text-muted-3">Slóð</dt>
                    <dd className="break-all font-mono">{cred.loginUrl}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-24 text-muted-3">Notandanafn</dt>
                    <dd className="break-all font-mono">{cred.email}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-24 text-muted-3">Lykilorð</dt>
                    <dd className="break-all font-mono">{cred.tempPassword}</dd>
                  </div>
                </dl>
                <div className="mt-2 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={copyCredentials}
                    className="text-[11.5px] font-semibold text-brand hover:underline"
                  >
                    {copied ? "Afritað!" : "Afrita upplýsingar"}
                  </button>
                  <a
                    href={`mailto:${cred.email}?subject=${encodeURIComponent("Aðgangur að Atomika")}&body=${encodeURIComponent(`Slóð: ${cred.loginUrl}\nNotandanafn: ${cred.email}\nLykilorð: ${cred.tempPassword}\n\nÞú þarft að velja nýtt lykilorð við fyrstu innskráningu.`)}`}
                    className="text-[11.5px] text-muted-2 hover:underline"
                  >
                    Opna í tölvupóstforriti
                  </a>
                </div>
              </div>
            )}
          </div>

          {role === "user" && accessProfile === "custom" && (
            <div>
              <label className={label}>Aðgangur að Herbergjum</label>
              <div className="mt-1.5 space-y-1.5">
                {AREAS.map((a) => (
                  <label
                    key={a.id}
                    className="flex cursor-pointer items-center gap-2 text-[13px] text-ink"
                  >
                    <input
                      type="checkbox"
                      checked={areaIds.includes(a.id)}
                      onChange={() => toggleArea(a.id)}
                      className="h-4 w-4 rounded border-line accent-brand"
                    />
                    {a.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {error && <p className="text-[12.5px] text-danger">{error}</p>}

          <div className="flex items-center justify-between pt-1">
            <button
              onClick={remove}
              disabled={saving}
              className="rounded-lg border border-danger/30 px-3 py-2 text-[12.5px] font-semibold text-danger hover:bg-danger/10 disabled:opacity-50"
            >
              Eyða
            </button>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="rounded-lg border border-line px-4 py-2 text-[13px] font-semibold text-ink hover:bg-canvas"
              >
                Hætta við
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="rounded-lg bg-brand px-4 py-2 text-[13px] font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
              >
                {saving ? "Vista…" : "Vista"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
